/**
 * Extract and validate redirect URI from URL params
 */
export const getRedirectUri = () => {
  const params = new URLSearchParams(window.location.search);
  const redirectUri = params.get('redirect_uri') || params.get('redirectUri');
  
  if (!redirectUri) {
    console.log('No redirect URI in URL params');
    return null;
  }

  console.log('Redirect URI received:', redirectUri);
  
  try {
    // Validate that it's a proper URL
    const url = new URL(redirectUri);
    console.log('Parsed redirect URI hostname:', url.hostname);

    // Only allow http(s) targets - blocks javascript:/data: style URIs
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      console.error('Redirect URI uses a disallowed protocol:', url.protocol);
      return null;
    }

    console.log('Parsed redirect URI, hostname:', url.hostname);
    return redirectUri;
  } catch (e) {
    console.error('Invalid redirect URI format:', redirectUri, e);
    return null;
  }
};

/**
 * Whether a redirect URI's hostname is trusted to receive an SSO token.
 *
 * Two ways a hostname can be trusted:
 *  - It's in the static REACT_APP_ALLOWED_APPS list (Kit19's own apps, localhost, etc.)
 *  - The backend recognizes it as a registered partner domain, i.e. the
 *    Authorize/DomainInfo lookup for that hostname (see DomainContext) succeeded.
 *    This is what allows partners on their own custom domains to use SSO
 *    without hardcoding every partner domain in the frontend.
 */
export const isRedirectHostnameTrusted = (redirectUri, domainConfig) => {
  if (!redirectUri) {
    return false;
  }

  let hostname;
  try {
    hostname = new URL(redirectUri).hostname;
  } catch (e) {
    return false;
  }

  const staticAllowedApps = (process.env.REACT_APP_ALLOWED_APPS || '')
    .split(',')
    .map((app) => app.trim())
    .filter(Boolean);

  if (staticAllowedApps.includes(hostname)) {
    return true;
  }

  // Backend has a domain record for this hostname => registered partner domain
  return !!domainConfig;
};

/**
 * Extract configuration from URL params
 */
export const getAppConfig = () => {
  const params = new URLSearchParams(window.location.search);
  const configParam = params.get('config');
  
  if (!configParam) {
    return null;
  }
  
  try {
    const config = JSON.parse(decodeURIComponent(configParam));
    return config;
  } catch (e) {
    console.error('Error parsing config:', e);
    return null;
  }
};

/**
 * Get app name from redirect URI or query param
 */
export const getAppName = () => {
  const params = new URLSearchParams(window.location.search);
  const appName = params.get('app') || params.get('appName');
  
  if (appName) {
    return appName;
  }

  // Try to extract from redirect URI
  const redirectUri = getRedirectUri();
  if (redirectUri) {
    try {
      const url = new URL(redirectUri);
      const hostname = url.hostname;
      
      // Extract app name from subdomain (e.g., whatsapp.kit19.com -> whatsapp)
      const parts = hostname.split('.');
      if (parts.length > 2) {
        return parts[0];
      }
    } catch (e) {
      console.error('Error extracting app name:', e);
    }
  }

  return 'CRM';
};

/**
 * Redirect back to app with token and configuration
 */
export const redirectToApp = (redirectUri, token, userData, appConfig = null, deviceId = null) => {
  console.log('redirectToApp called with:', { redirectUri, token: token ? '***' : null, userData, appConfig, deviceId });
  
  if (!redirectUri) {
    console.error('No redirect URI provided for redirect');
    return;
  }

  if (!token) {
    console.error('No token provided for redirect');
    return;
  }

  try {
    // Use configured redirect URL if provided, otherwise use the redirectUri
    const finalRedirectUrl = appConfig?.redirectUrl || redirectUri;
    console.log('Final redirect URL:', finalRedirectUrl);
    
    const url = new URL(finalRedirectUrl);
    
    // Add token and user data as query params
    url.searchParams.set('token', token);
    url.searchParams.set('user_id', userData?.userId || userData?.User_ID || '');
    url.searchParams.set('sso', 'true');
    
    // Add device ID if provided
    if (deviceId) {
      url.searchParams.set('deviceId', deviceId);
    }
    
    // Add additional config params if needed
    if (appConfig) {
      url.searchParams.set('config', encodeURIComponent(JSON.stringify(appConfig)));
    }
    
    const finalUrl = url.toString();
    console.log('Redirecting to:', finalUrl);
    
    // Redirect to app
    window.location.href = finalUrl;
  } catch (e) {
    console.error('Error redirecting to app:', e);
    console.error('Redirect URI:', redirectUri);
    console.error('Token:', token ? 'present' : 'missing');
  }
};

/**
 * Build SSO login URL for redirecting from apps
 */
export const buildSSOLoginUrl = (currentAppUrl, appName) => {
  const ssoUrl = new URL(window.location.origin + '/login');
  ssoUrl.searchParams.set('redirect_uri', currentAppUrl);
  ssoUrl.searchParams.set('app', appName);
  return ssoUrl.toString();
};

/**
 * Extract token from URL params (for callback page)
 */
export const extractTokenFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  
  return params.get('token') || hashParams.get('token') || null;
};
